import { useState, useCallback, useEffect, useRef } from 'react';
import { getApiUrl } from '../config';
import type { GroupListItem, GroupDetail, GroupInvitationReceived } from '../types';

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export function useGroups(options?: { onError?: (message: string) => void }) {
  const [groups, setGroups] = useState<GroupListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const onErrorRef = useRef(options?.onError);
  useEffect(() => {
    onErrorRef.current = options?.onError;
  }, [options?.onError]);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl('/api/groups'), { headers: getAuthHeaders() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onErrorRef.current?.(data.error || 'Error al cargar grupos');
        return;
      }
      const data = await res.json();
      setGroups(data.groups || []);
    } catch {
      onErrorRef.current?.('Error de conexión');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchGroups().finally(() => setLoading(false));
  }, [fetchGroups]);

  const refetch = useCallback(() => {
    setLoading(true);
    fetchGroups().finally(() => setLoading(false));
  }, [fetchGroups]);

  return { groups, loading, refetch, fetchGroups };
}

export function useGroupDetail(
  groupId: string | undefined,
  options?: { onError?: (message: string) => void }
) {
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [loading, setLoading] = useState(!!groupId);
  const onErrorRef = useRef(options?.onError);
  useEffect(() => {
    onErrorRef.current = options?.onError;
  }, [options?.onError]);

  const fetchGroup = useCallback(async (id: string) => {
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}`), { headers: getAuthHeaders() });
      if (!res.ok) {
        if (res.status === 403) onErrorRef.current?.('No tenés acceso a este grupo');
        else if (res.status === 404) onErrorRef.current?.('Grupo no encontrado');
        else {
          const data = await res.json().catch(() => ({}));
          onErrorRef.current?.(data.error || 'Error al cargar el grupo');
        }
        setGroup(null);
        return;
      }
      const data = await res.json();
      setGroup(data.group || null);
    } catch {
      onErrorRef.current?.('Error de conexión');
      setGroup(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!groupId) {
      setGroup(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchGroup(groupId);
  }, [groupId, fetchGroup]);

  return { group, loading, refetch: () => groupId && fetchGroup(groupId) };
}

export function useCreateGroup(options?: {
  onError?: (message: string) => void;
  onSuccess?: (group: GroupListItem) => void;
}) {
  const [creating, setCreating] = useState(false);
  const onErrorRef = useRef(options?.onError);
  const onSuccessRef = useRef(options?.onSuccess);
  useEffect(() => {
    onErrorRef.current = options?.onError;
    onSuccessRef.current = options?.onSuccess;
  }, [options?.onError, options?.onSuccess]);

  const createGroup = useCallback(
    async (name: string, description?: string, memberIds?: string[]) => {
      setCreating(true);
      try {
        const res = await fetch(getApiUrl('/api/groups'), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            name: name.trim(),
            description: description?.trim() || '',
            memberIds: memberIds || [],
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          onErrorRef.current?.(data.error || 'Error al crear el grupo');
          return null;
        }
        const created = data.group;
        onSuccessRef.current?.(created);
        return created;
      } catch {
        onErrorRef.current?.('Error de conexión');
        return null;
      } finally {
        setCreating(false);
      }
    },
    []
  );

  return { createGroup, creating };
}

export function useGroupInvitations(options?: {
  onError?: (message: string) => void;
  onAccept?: (groupId: string) => void;
  onReject?: () => void;
}) {
  const [invitations, setInvitations] = useState<GroupInvitationReceived[]>([]);
  const [loading, setLoading] = useState(true);
  const onErrorRef = useRef(options?.onError);
  const onAcceptRef = useRef(options?.onAccept);
  const onRejectRef = useRef(options?.onReject);
  useEffect(() => {
    onErrorRef.current = options?.onError;
    onAcceptRef.current = options?.onAccept;
    onRejectRef.current = options?.onReject;
  }, [options?.onError, options?.onAccept, options?.onReject]);

  const fetchInvitations = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl('/api/groups/invitations'), { headers: getAuthHeaders() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onErrorRef.current?.(data.error || 'Error al cargar invitaciones');
        return;
      }
      const data = await res.json();
      setInvitations(data.invitations || []);
    } catch {
      onErrorRef.current?.('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchInvitations();
  }, [fetchInvitations]);

  const accept = useCallback(
    async (invitationId: string) => {
      try {
        const res = await fetch(getApiUrl(`/api/groups/invitations/${invitationId}/accept`), {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          onErrorRef.current?.(data.error || 'Error al aceptar');
          return null;
        }
        setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
        const groupId = data.groupId as string | undefined;
        if (groupId) onAcceptRef.current?.(groupId);
        return groupId;
      } catch {
        onErrorRef.current?.('Error de conexión');
        return null;
      }
    },
    []
  );

  const reject = useCallback(async (invitationId: string) => {
    try {
      const res = await fetch(getApiUrl(`/api/groups/invitations/${invitationId}/reject`), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onErrorRef.current?.(data.error || 'Error al rechazar');
        return;
      }
      setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
      onRejectRef.current?.();
    } catch {
      onErrorRef.current?.('Error de conexión');
    }
  }, []);

  return { invitations, loading, refetch: fetchInvitations, accept, reject };
}
